import { reverse } from 'lodash'
import routes from './routes'
import deviceUtil from '../utils/device.util'
import { i18n } from "../i18n/i18n";

export type MenuRouteGroupName = 'dashboard' | 'receiving' | 'production' | 'inventory' | 'shipping' | 'report' | 'management' | 'labels' | 'payments'

export interface Collapsible {
  text?: string
  icon?: string
  subsidiaries: MenuRoute[]
}

export interface MenuRoute {
  name?: string
  text?: string
  icon?: string
  administrative?: boolean
  advanced?: boolean
  to: string
}

export interface MenuRouteGroup extends MenuRoute {
  name: MenuRouteGroupName
  subsidiaries: (MenuRoute | Collapsible)[]
}

const menuRoutes: MenuRouteGroup[] = [
  {
    name: 'dashboard',
    text: `${i18n("Dashboard")}`,
    icon: 'dashboard',
    to: routes.dashboard,
    get subsidiaries () {
      return [
        {
          name: this.name,
          text: `${i18n("Dashboard")}`,
          icon: 'dashboard',
          to: this.to
        }
      ]
    }
  },
  {
    name: 'receiving',
    text: `${i18n("Receiving")}`,
    icon: 'install',
    to: routes.purchaseOrders,
    get subsidiaries () {
      return [
        {
          name: 'purchaseOrders',
          text: `${i18n("Purchase orders")}`,
          icon: 'file-list',
          to: this.to
        },
        {
          name: 'receivings',
          text: `${i18n("Receivings")}`,
          icon: 'send-plane-2',
          to: routes.receivings
        },
        { to: routes.purchaseOrderEnter },
        { to: routes.purchaseOrdersImport },
        { to: routes.purchaseOrder },
        { to: routes.purchaseOrderEdit },
        { to: routes.receivingCreate }
      ]
    }
  },
  {
    name: 'production',
    text: `${i18n("Production")}`,
    icon: 'archive',
    to: deviceUtil.isMobile() ? routes.inventoryCreate : routes.workOrders,
    get subsidiaries () {
      const base = [
        {
          name: 'caseAssignment',
          text: `${i18n("Case Assignment")}`,
          icon: 'inbox-archive',
          to: routes.caseAssignment
        },
        {
          name: 'packTraceCreate',
          text: `${i18n("Pack Trace")}`,
          icon: 'road-map',
          advanced: true,
          to: routes.packTraceCreate
        },
        {
          name: 'packTraces',
          text: `${i18n("Pack Traces")}`,
          icon: 'bar-chart-box',
          advanced: true,
          to: routes.packTraces
        },
        {
          name: 'labelTrace',
          text: `${i18n("Label Traces")}`,
          icon: 'treasure-map',
          advanced: true,
          to: routes.labelTrace
        },
        { to: routes.workOrderCalendar },
        { to: routes.workOrderCreate },
        { to: routes.workOrder },
        { to: routes.workOrderEdit },
        { to: routes.packTrace }
      ]
      const workOrders = {
        name: 'workOrders',
        text: `${i18n("Work Orders")}`,
        icon: 'file-list',
        to: routes.workOrders
      }
      const inventoryCreate = {
        name: 'inventoryCreate',
        text: `${i18n("Create Inventory")}`,
        icon: 'add-circle',
        to: routes.inventoryCreate
      }
      let dynamic = [inventoryCreate, workOrders]
      if (!deviceUtil.isMobile()) {
        dynamic = reverse(dynamic)
      }
      return [...dynamic, ...base]
    }
  },
  {
    name: 'inventory',
    text: `${i18n("Inventory")}`,
    icon: 'stack',
    to: routes.inventories,
    get subsidiaries () {
      return [
        {
          name: 'inventories',
          text: `${i18n("Inventories")}`,
          icon: 'stack',
          to: this.to
        },
        {
          text: `${i18n("Transfer")}`,
          icon: 'route',
          subsidiaries: [
            {
              name: 'transfer',
              text: `${i18n("Transfer")}`,
              icon: 'route',
              to: routes.transfer
            },
            {
              name: 'caseTransfer',
              text: `${i18n("Case Transfer")}`,
              icon: 'stackshare',
              to: routes.caseTransfer
            },
            {
              name: 'quantityTransfer',
              text: `${i18n("Quantity Transfer")}`,
              icon: 'share-forward-box',
              to: routes.quantityTransfer
            },
            {
              name: 'bulkTransfer',
              text: `${i18n("Bulk Transfer")}`,
              icon: 'caravan',
              advanced: true,
              to: routes.bulkTransfer
            }
          ]
        },
        {
          name: 'stockOnHand',
          text: `${i18n("Stock on Hand")}`,
          icon: 'shield-check',
          to: routes.stockOnHand
        },
        {
          name: 'palletDetails',
          text: `${i18n("Pallet Details")}`,
          icon: 'qr-scan-2',
          to: routes.palletDetails
        },
        {
          name: 'barePallets',
          text: `${i18n("Bare Pallets")}`,
          icon: 'checkbox-blank',
          to: routes.barePallets
        },
        {
          name: 'standaloneCases',
          text: `${i18n("Standalone Cases")}`,
          icon: 'archive',
          to: routes.standaloneCases
        },
        {
          name: 'locationSearch',
          text: `${i18n("Location Search")}`,
          icon: 'map-pin-5',
          to: routes.locationSearch
        },
        { to: routes.inventory },
        { to: routes.inventoryHistory },
        { to: routes.barePalletCreate },
        { to: routes.standaloneCaseCreate }
      ]
    }
  },
  {
    name: 'shipping',
    text: `${i18n("Shipping")}`,
    icon: 'truck',
    to: deviceUtil.isMobile() ? routes.ship : routes.shipOrders,
    get subsidiaries () {
      const base = [
        {
          name: 'billsOfLading',
          text: `${i18n("Bill of Lading")}`,
          icon: 'bill',
          to: routes.billsOfLading
        },
        { to: routes.shipOrderCreate },
        { to: routes.shipOrder },
        { to: routes.shipOrderPallets },
        { to: routes.shipOrderEdit },
        { to: routes.billOfLadingGenerate }
      ]
      const ship = {
        name: 'ship',
        text: `${i18n("Ship")}`,
        icon: 'truck',
        to: routes.ship
      }
      const shipOrders = {
        name: 'shipOrders',
        text: `${i18n("Ship Orders")}`,
        icon: 'file-list',
        to: routes.shipOrders
      }
      let dynamic = [ship, shipOrders]
      if (!deviceUtil.isMobile()) {
        dynamic = reverse(dynamic)
      }
      return [...dynamic, ...base]
    }
  },
  {
    name: 'report',
    text: `${i18n("Report")}`,
    icon: 'pie-chart',
    to: routes.inventoryReport,
    get subsidiaries () {
      return [
        {
          name: 'inventoryReport',
          text: `${i18n("Inventory")}`,
          icon: 'stack',
          to: this.to
        },
        {
          name: 'packTraceReport',
          text: `${i18n("Pack Trace")}`,
          icon: 'road-map',
          advanced: true,
          to: routes.packTraceReport
        },
        {
          name: 'labelTraceReport',
          text: `${i18n("Label Trace")}`,
          icon: 'treasure-map',
          advanced: true,
          to: routes.labelTraceReport
        },
        {
          name: 'shippingReport',
          text: `${i18n("Shipping")}`,
          icon: 'truck',
          to: routes.shippingReport
        },
        {
          name: 'insAndOutsReport',
          text: `${i18n("Ins and Outs")}`,
          icon: 'route',
          to: routes.insAndOutsReport
        }
      ]
    }
  },
  {
    name: 'labels',
    text: `${i18n("Labels")}`,
    icon: 'bill',
    to: routes.customLabels,
    get subsidiaries () {
      return [
        {
          name: 'customLabels',
          text: `${i18n("Custom Labels")}`,
          icon: 'bill',
          administrative: true,
          to: routes.customLabels
        },
        {
          name: 'systemLabels',
          text: `${i18n("System Labels")}`,
          icon: 'bill',
          administrative: true,
          to: routes.systemLabels
        }
        /*{
          name: 'labelBuilder',
          text: 'Label Builder',
          icon: 'pencil-ruler',
          administrative: true,
          to: routes.labelBuilder
        },*/
      ]
    }
  },
  {
    name: 'management',
    text: `${i18n("Management")}`,
    icon: 'settings',
    to: routes.items,
    get subsidiaries () {
      return [
        {
          name: 'items',
          text: `${i18n("Items")}`,
          icon: 'file-list',
          administrative: true,
          to: this.to
        },
        {
          name: 'categories',
          text: `${i18n("Categories")}`,
          icon: 'file-copy-2',
          administrative: true,
          to: routes.categories
        },
        {
          name: 'locations',
          text: `${i18n("Locations")}`,
          icon: 'map-pin-5',
          administrative: true,
          to: routes.locations
        },
        {
          name: 'contacts',
          text: `${i18n("Contacts")}`,
          icon: 'contacts-book-2',
          administrative: true,
          to: routes.contacts
        },
        {
          name: 'users',
          text: `${i18n("Users")}`,
          icon: 'group',
          administrative: true,
          to: routes.users
        },
        {
          name: 'licensePlates',
          text: `${i18n("Custom LPNs")}`,
          icon: 'fingerprint',
          administrative: true,
          to: routes.licensePlates
        },
        { to: routes.itemCreate },
        { to: routes.itemEdit },
        { to: routes.categoryCreate },
        { to: routes.categoryEdit },
        { to: routes.locationCreate },
        { to: routes.locationEdit },
        { to: routes.contactCreate },
        { to: routes.contactEdit },
        { to: routes.userCreate },
        { to: routes.userEdit },
        { to: routes.licensePlateUpload },
        { to: routes.customLabelCreate },
        { to: routes.customLabel },
        { to: routes.customLabelPrint },
        { to: routes.customLabelPrintsPaginate },
        { to: routes.customLabelEdit }
      ]
    }
  },
  {
    name: 'payments',
    text: `${i18n("Account")}`,
    icon: 'account-circle',
    to: routes.userSubscriptions,
    get subsidiaries () {
      return [
        {
          name: 'payments',
          text: `${i18n("Subscriptions")}`,
          icon: 'exchange-dollar',
          administrative: true,
          to: routes.userSubscriptions
        },
      ]
    }
  },
]

export default menuRoutes
